import { useEffect, useRef, useState } from "react";
import { Clock, Calendar, AlertCircle, UserRound, Check, MessageCircle, Ban, RefreshCw } from "lucide-react";
import { utcToZonedTime, format } from "date-fns-tz";
import CalendarService from "../../services/calendar/CalendarService";
import { showError } from "../../components/alertas";
import { CountryPhoneSelector } from "../../components/CountryPhoneSelector";
import { extractPhoneWithoutCountryCode } from "../../services/countries/countryData";
import { detectCountryFromPhone } from "../../services/countries/phoneNormalizer";
import Sheet from "../../components/sheet/Sheet";
import useMediaQuery from "../../hooks/useMediaQuery";
import logger from "../../utils/logger";

const TIME_ZONE = "America/Mexico_City";

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr + "T12:00:00");
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const sub = `${date.getDate()} ${date.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '')}`;
  if (date.toDateString() === today.toDateString()) return { label: "Hoy", sub };
  if (date.toDateString() === tomorrow.toDateString()) return { label: "Mañana", sub };
  const d = date.toLocaleDateString('es-MX', { weekday: 'short' }).replace('.', '');
  return { label: d.charAt(0).toUpperCase() + d.slice(1), sub };
};
const longDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  const s = d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
/** "lun 21 sep": para el botón fijo, donde el nombre largo del día no cabe en móvil. */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '');
};

/**
 * Reserva pública: profesional (si hay varios) → día → hora → datos. Móvil primero:
 * tira de fechas, rejilla de horas, botón fijo abajo y sheets para elegir profesional,
 * confirmar y ver el resultado. La lógica de disponibilidad y alta no cambió.
 */
export default function CalendarView({ setCurrentView, selectedService, clientData }) {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const side = isDesktop ? "right" : "bottom";
  const staff = clientData.staff || [];
  const multiStaff = staff.length > 1;
  const staffTerm = clientData.personalization?.staffLabel?.trim() || "Profesional";

  const [availableDays, setAvailableDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  // Días sin un solo hueco: se marcan "Lleno" en la tira en vez de desaparecer (antes el
  // backend los quitaba de la lista; los clientes preguntaban "¿para tal día no tienes
  // espacio?" sin poder verlo). Se siembra con `hasAvailability` al cargar la lista, y
  // también se agrega aquí si un día se queda sin hueco justo al consultar sus horas.
  const [fullDays, setFullDays] = useState(() => new Set());
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingDays, setLoadingDays] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showStaffSheet, setShowStaffSheet] = useState(false);
  const [selectedBarber, setSelectedBarber] = useState(staff.length === 1 ? staff[0].id : "");
  const [showConfirm, setShowConfirm] = useState(false);
  const [nombre, setNombre] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(() => new URLSearchParams(window.location.search).get('pais') || 'MX');
  const [telefono, setTelefono] = useState(() => new URLSearchParams(window.location.search).get('telefono') || "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // 'ok' | 'blocked'
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (multiStaff) { setShowStaffSheet(true); setSelectedBarber(""); }
    else if (staff.length === 1) { setSelectedBarber(staff[0].id); setShowStaffSheet(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData.staff]);

  // Teléfono por URL (links del bot): detectar país si no viene y quitar la lada.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pais = params.get('pais'); const tel = params.get('telefono');
    if (tel && !pais) {
      const detected = detectCountryFromPhone(tel);
      if (detected) { setSelectedCountry(detected); setTelefono(extractPhoneWithoutCountryCode(tel, detected)); }
    } else if (tel && pais) {
      setTelefono(extractPhoneWithoutCountryCode(tel, pais));
    }
  }, []);

  // Los días se cargan al entrar y al cambiar de profesional; NO al cambiar de día:
  // antes `selectedDate` estaba en las dependencias y cada toque en un día volvía a
  // pedir la lista, la tira se desmontaba (skeleton) y al volver quedaba en el inicio,
  // así que el día elegido al final de la tira "desaparecía" de la vista.
  useEffect(() => {
    if (multiStaff && !selectedBarber) return;
    let cancelled = false;
    (async () => {
      setLoadingDays(true);
      try {
        const response = await CalendarService.getAvailableDays(clientData.id, selectedBarber, true);
        if (cancelled) return;
        const days = response.data.map(d => ({ ...d, dateStr: d.date.split('T')[0] }));
        setAvailableDays(days);
        // Días sin hueco ya vienen marcados (`hasAvailability: false`): no esperar a que el
        // cliente los toque para saber que están llenos, como antes (ver estado `fullDays`).
        setFullDays(new Set(days.filter((d) => d.hasAvailability === false).map((d) => d.dateStr)));
        if (days.length > 0) {
          const firstOpen = days.find((d) => d.hasAvailability !== false) || days[0];
          setSelectedDate((prev) => prev || firstOpen.dateStr);
        }
      } catch (error) {
        logger.error("Error al cargar días disponibles:", error);
      } finally {
        if (!cancelled) setLoadingDays(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedService, clientData, selectedBarber, multiStaff]);

  // Traer el día elegido a la vista en la tira (scroll manual: scrollIntoView movería la página).
  const stripRef = useRef(null);
  useEffect(() => {
    const el = stripRef.current;
    const active = el?.querySelector(".cp-choice.is-active");
    if (!el || !active) return;
    const left = active.offsetLeft - el.offsetLeft;
    const target = left - (el.clientWidth - active.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [selectedDate, loadingDays]);

  useEffect(() => {
    if (!selectedDate || (multiStaff && !selectedBarber)) return;
    let cancelled = false;
    (async () => {
      setLoadingSlots(true);
      setSelectedTime(null);
      try {
        const response = await CalendarService.getAvailableSlots(clientData.id, selectedDate, selectedService.durationMinutes, selectedBarber);
        if (cancelled) return;
        const now = utcToZonedTime(new Date(), TIME_ZONE);
        let slots = response.data;
        if (new Date(selectedDate + "T12:00:00").toDateString() === now.toDateString()) {
          const currentTime = format(now, "HH:mm", { timeZone: TIME_ZONE });
          slots = slots.filter(slot => slot.start > currentTime);
        }
        setAvailableSlots(slots);
        if (slots.length === 0) setFullDays((prev) => new Set(prev).add(selectedDate));
      } catch (error) {
        logger.error("Error al cargar horarios:", error);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate, selectedService, clientData, selectedBarber, multiStaff]);

  const barberName = staff.find(s => s.id === selectedBarber)?.name || "";
  const chooseBarber = (id) => {
    setSelectedBarber(id); setSelectedDate(null); setAvailableDays([]); setAvailableSlots([]); setSelectedTime(null); setFullDays(new Set());
    setShowStaffSheet(false);
  };

  const reloadAvailableDays = async () => {
    try {
      const response = await CalendarService.getAvailableDays(clientData.id, selectedBarber, true);
      const days = response.data.map(d => ({ ...d, dateStr: d.date.split('T')[0] }));
      setAvailableDays(days);
      setFullDays(new Set(days.filter((d) => d.hasAvailability === false).map((d) => d.dateStr)));
    } catch (error) {
      logger.error("Error al recargar días disponibles:", error);
    }
  };

  const submit = async () => {
    const phoneWithoutCode = extractPhoneWithoutCountryCode(telefono, selectedCountry);
    // Rechazar basura obvia (todos los dígitos iguales) sin gastar la llamada al backend.
    const soloDigitos = (phoneWithoutCode || "").replace(/\D/g, "");
    if (soloDigitos.length > 0 && /^(\d)\1+$/.test(soloDigitos)) { setPhoneError("Ingresa un número de teléfono válido."); return; }
    setSaving(true);
    try {
      await CalendarService.createAppointment({
        clientId: clientData.id, service: selectedService, date: new Date(selectedDate + "T12:00:00"), time: selectedTime,
        nombre, telefono: phoneWithoutCode, pais: selectedCountry, staffId: staff.length === 1 ? staff[0].id : selectedBarber,
      });
      setShowConfirm(false);
      setResult("ok");
      setNombre("");
      setTelefono(new URLSearchParams(window.location.search).get('telefono') || "");
      setSelectedTime(null);
      setAvailableSlots([]);
      await reloadAvailableDays();
    } catch (e) {
      const code = e.response?.data?.error;
      if (code === 'BLOCKED_NUMBER') { setShowConfirm(false); setResult("blocked"); }
      else if (code === 'SLOT_TAKEN') showError("Ese horario se acaba de ocupar. Elige otro, por favor.");
      else if (code === 'WHATSAPP_NUMBER_NOT_FOUND') { setPhoneError("Este número no tiene WhatsApp. Revísalo e inténtalo de nuevo."); showError("El número no tiene WhatsApp. Verifica que sea correcto."); }
      else showError("Error al agendar la cita. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedService) {
    return (
      <div className="cp-card"><div className="cp-empty"><AlertCircle size={36} /><h3>Primero elige un servicio</h3><p>Así te mostramos los días y horas disponibles.</p><button type="button" className="cp-btn cp-btn-primary" onClick={() => setCurrentView("services")}>Ver servicios</button></div></div>
    );
  }

  const needsAdvance = !!selectedService.requiresAdvance;
  const needsConfirm = !!clientData.personalization?.confirmWebAppointment;
  const bizPhone = clientData?.business?.phones?.[0];
  const canBook = !!selectedDate && !!selectedTime && (!multiStaff || !!selectedBarber);
  // Con un solo servicio la vista de servicios redirige aquí sola: "Otro servicio" no aplica.
  const singleService = (clientData?.business?.services?.length || 0) <= 1;

  return (
    <section className="cp-pub-book" translate="no">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title">Elige día y hora</h2>
          <p className="cp-page-sub">Solo te mostramos horarios con lugar.</p>
        </div>
      </header>

      <div className="cp-card cp-pub-summary">
        <span className="cp-pub-thumb"><Check size={20} /></span>
        <span className="cp-row-main">
          <span className="cp-row-title">{selectedService.name}</span>
          <span className="cp-row-sub">{selectedService.durationMinutes} min · ${selectedService.price}{multiStaff && barberName ? ` · con ${barberName}` : ""}</span>
          {/* Con un solo servicio nunca se vio la lista: la descripción va aquí. */}
          {singleService && selectedService.description && <span className="cp-row-sub">{selectedService.description}</span>}
        </span>
        <span className="cp-row-end">
          {multiStaff && <button type="button" className="cp-btn" onClick={() => setShowStaffSheet(true)}><UserRound size={15} /> {barberName ? "Cambiar" : `Elegir ${staffTerm.toLowerCase()}`}</button>}
          {!singleService && <button type="button" className="cp-linkbtn" onClick={() => setCurrentView("services")}>Otro servicio</button>}
        </span>
      </div>

      {multiStaff && !selectedBarber ? (
        <div className="cp-card"><div className="cp-empty"><UserRound size={32} /><h3>¿Con quién?</h3><p>Elige a tu {staffTerm.toLowerCase()} para ver su disponibilidad.</p><button type="button" className="cp-btn cp-btn-primary" onClick={() => setShowStaffSheet(true)}>Elegir {staffTerm.toLowerCase()}</button></div></div>
      ) : (
        <>
          <article className="cp-card">
            <div className="cp-card-head"><h3 className="cp-card-title"><Calendar size={18} /> Día</h3></div>
            {loadingDays ? (
              <div className="cp-choice-strip">{[0, 1, 2, 3, 4].map(i => <div key={i} className="cp-skeleton" style={{ width: 68, height: 52, flex: "0 0 auto" }} />)}</div>
            ) : availableDays.length === 0 ? (
              <div className="cp-note"><AlertCircle size={16} /> No hay días disponibles por el momento.</div>
            ) : (
              <div className="cp-choice-strip" ref={stripRef}>
                {availableDays.map((day) => {
                  const { label, sub } = formatDateLabel(day.dateStr);
                  const full = fullDays.has(day.dateStr);
                  return (
                    <button key={day.dateStr} type="button" className={`cp-choice ${selectedDate === day.dateStr ? "is-active" : ""} ${full ? "is-full" : ""}`} onClick={() => setSelectedDate(day.dateStr)}>
                      {label}<small>{full ? "Lleno" : sub}</small>
                    </button>
                  );
                })}
              </div>
            )}
          </article>

          {selectedDate && (
            <article className="cp-card">
              <div className="cp-card-head"><h3 className="cp-card-title"><Clock size={18} /> Hora</h3><span className="cp-hint">{longDate(selectedDate)}</span></div>
              {loadingSlots ? (
                <div className="cp-pub-slots">{[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="cp-skeleton" style={{ height: 48 }} />)}</div>
              ) : availableSlots.length === 0 ? (
                <div className="cp-note"><AlertCircle size={16} /><div>Ese día ya no tiene lugar. Prueba con otro.</div></div>
              ) : (
                <div className="cp-pub-slots">
                  {availableSlots.map((slot, idx) => (
                    <button key={slot.start + idx} type="button" className={`cp-choice ${selectedTime === slot.start ? "is-active" : ""}`} onClick={() => setSelectedTime(slot.start)}>{slot.start}</button>
                  ))}
                </div>
              )}
            </article>
          )}

          <div className="cp-pub-cta">
            <button type="button" className={`cp-btn cp-btn-primary cp-btn-block ${canBook ? "has-sub" : ""}`} disabled={!canBook} onClick={() => setShowConfirm(true)}>
              <Calendar size={18} />
              {canBook ? <span className="cp-pub-cta-text"><b>Agendar cita</b><small>{shortDate(selectedDate)} · {selectedTime}</small></span> : "Elige día y hora"}
            </button>
          </div>
        </>
      )}

      {/* Elegir profesional */}
      <Sheet open={showStaffSheet} onClose={() => setShowStaffSheet(false)} side={side} title="¿Con quién quieres tu cita?" subtitle={`Elige a tu ${staffTerm.toLowerCase()}`} className="cp-vars">
        <div className="cp-pub-staff">
          {staff.map(s => (
            <button key={s.id} type="button" className={`cp-choice ${selectedBarber === s.id ? "is-active" : ""}`} onClick={() => chooseBarber(s.id)}>
              <span className="cp-avatar">
                {s.avatarSeed ? <img src={`https://api.dicebear.com/9.x/${clientData.personalization?.avatarStyle || 'notionists'}/svg?seed=${s.avatarSeed}`} alt="" /> : s.name.charAt(0).toUpperCase()}
              </span>
              {s.name}
            </button>
          ))}
        </div>
      </Sheet>

      {/* Confirmar: datos del cliente */}
      <Sheet open={showConfirm} onClose={() => !saving && setShowConfirm(false)} side={side} title="Confirma tu cita" subtitle={`${selectedService.name} · ${longDate(selectedDate)}, ${selectedTime}${multiStaff && barberName ? ` · ${barberName}` : ""}`} className="cp-vars"
        footer={
          <div className="cp-sheet-actions">
            <button type="button" className="cp-btn" onClick={() => setShowConfirm(false)} disabled={saving}>Cancelar</button>
            <button type="button" className="cp-btn cp-btn-primary" onClick={submit} disabled={saving || !nombre.trim() || !telefono || !!phoneError}>
              {saving ? <><span className="cp-spin" /> Agendando…</> : "Agendar"}
            </button>
          </div>
        }
      >
        <div className="cp-form" style={{ paddingTop: "0.25rem" }}>
          <label className="cp-field"><span>Tu nombre</span><input type="text" className="cp-input" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre y apellido" autoComplete="name" /></label>
          <div className="cp-field">
            <span>Tu WhatsApp</span>
            <CountryPhoneSelector selectedCountry={selectedCountry} onCountryChange={setSelectedCountry} phoneValue={telefono} onPhoneChange={setTelefono} phoneError={phoneError} setPhoneError={setPhoneError} placeholder="Número de teléfono" />
            <small>Ahí te llega la confirmación y los recordatorios.</small>
          </div>
          {needsAdvance && <div className="cp-note"><AlertCircle size={16} /><div>Este servicio pide un anticipo de <b>${selectedService.advanceAmount}</b>. Te decimos cómo pagarlo por WhatsApp.</div></div>}
        </div>
      </Sheet>

      {/* Resultado */}
      <Sheet open={result === "ok"} onClose={() => setResult(null)} side={side} title="" className="cp-vars"
        footer={
          <div className="cp-sheet-actions is-single">
            {(needsAdvance || needsConfirm) && bizPhone
              ? <a className="cp-btn cp-btn-primary" href={`https://wa.me/${bizPhone}?text=confirmar`} target="_blank" rel="noopener noreferrer"><MessageCircle size={16} /> Confirmar en WhatsApp</a>
              : <button type="button" className="cp-btn cp-btn-primary" onClick={() => { setResult(null); setSelectedDate(null); setSelectedTime(null); }}><RefreshCw size={16} /> Agendar otra cita</button>}
            {!singleService && <button type="button" className="cp-btn" onClick={() => { setResult(null); setCurrentView("services"); }}>Volver a servicios</button>}
          </div>
        }
      >
        <div className="cp-pub-result">
          <span className={`cp-pub-result-icon ${needsAdvance || needsConfirm ? "is-wait" : ""}`}>{needsAdvance || needsConfirm ? <Clock size={34} /> : <Check size={34} strokeWidth={3} />}</span>
          {needsAdvance ? (
            <><h3>Falta tu anticipo</h3><p>Tu cita queda apartada cuando el negocio confirme el pago de <b>${selectedService.advanceAmount}</b>. Revisa tu WhatsApp: ahí te dicen cómo pagarlo. Puede tardar unos minutos.</p></>
          ) : needsConfirm ? (
            <><h3>Cita solicitada</h3><p>Revisa tu WhatsApp y confirma en los próximos 5 minutos, o el lugar se libera.</p></>
          ) : (
            <><h3>¡Cita agendada!</h3><p>Te mandamos los detalles a tu WhatsApp. Ahí también te recordamos antes de la cita.</p></>
          )}
        </div>
      </Sheet>

      <Sheet open={result === "blocked"} onClose={() => setResult(null)} side={side} title="" className="cp-vars"
        footer={
          <div className={`cp-sheet-actions ${bizPhone ? "" : "is-single"}`}>
            {bizPhone && <a className="cp-btn cp-btn-primary" href={`https://wa.me/${bizPhone}`} target="_blank" rel="noopener noreferrer"><MessageCircle size={16} /> Escribir al negocio</a>}
            <button type="button" className="cp-btn" onClick={() => { setResult(null); setCurrentView("services"); }}>Volver</button>
          </div>
        }
      >
        <div className="cp-pub-result">
          <span className="cp-pub-result-icon is-no"><Ban size={32} /></span>
          <h3>No pudimos agendar</h3>
          <p>Con este número no es posible reservar por aquí. Si crees que es un error, escríbele directo al negocio.</p>
        </div>
      </Sheet>
    </section>
  );
}
